import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { boApi, errMsg } from "./api";
import { useBo } from "./AdminAuth";
import { SimBadge } from "../components/SimBadge";

function Shell({ children, title, sub }) {
  return (
    <div className="dark flex min-h-screen items-center justify-center bg-background p-4 text-foreground">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-7 shadow-2xl">
        <div className="mb-4"><SimBadge testid="bo-login-sim-badge" /></div>
        <div className="mb-6 flex items-center gap-2.5">
          <ShieldCheck className="h-6 w-6 text-primary" />
          <div>
            <div className="font-display text-lg font-bold">{title}</div>
            <div className="text-xs text-muted-foreground">{sub}</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function AdminLogin() {
  const { signedIn } = useBo();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); setErr(""); try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); } };
  const login = (e) => { e.preventDefault(); run(async () => setStep((await boApi.post("/auth/login", { email, password })).data)); };
  const verify = (e) => { e.preventDefault(); run(async () => { const r = await boApi.post("/auth/verify", { challenge: step.challenge, code }); signedIn(r.data.token, r.data.admin); }); };
  return (
    <Shell title="Greenwick Back-office" sub="Simulated trading terminal admin · authorised staff only">
      {!step && (
        <form onSubmit={login} className="space-y-3" data-testid="bo-login-form">
          <Input data-testid="bo-login-email" type="email" placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <Input data-testid="bo-login-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          <Button data-testid="bo-login-submit" className="w-full" disabled={busy}>Continue</Button>
        </form>
      )}
      {step && (
        <form onSubmit={verify} className="space-y-3" data-testid="bo-2fa-form">
          {step.stage === "setup" ? (
            <div className="space-y-2 text-xs text-muted-foreground">
              <p>Two-factor authentication is mandatory. Scan this QR code with Google Authenticator, then enter the 6-digit code.</p>
              <div data-testid="bo-2fa-qr" className="mx-auto w-44 rounded bg-white p-2" dangerouslySetInnerHTML={{ __html: step.qr_svg }} />
              <p className="break-all text-center">Or enter key: <span data-testid="bo-2fa-secret" className="font-mono text-foreground">{step.secret}</span></p>
            </div>
          ) : <p className="text-xs text-muted-foreground">Enter the 6-digit code from your authenticator app.</p>}
          <Input data-testid="bo-2fa-code" inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required className="num text-center text-lg tracking-[0.4em]" />
          <Button data-testid="bo-2fa-submit" className="w-full" disabled={busy || code.length !== 6}>Verify & sign in</Button>
          <button type="button" onClick={() => { setStep(null); setCode(""); }} className="w-full text-xs text-muted-foreground">Back</button>
        </form>
      )}
      {err && <p data-testid="bo-login-error" className="mt-3 text-xs text-destructive">{err}</p>}
    </Shell>
  );
}

export function AcceptInvite() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { boApi.get(`/auth/invite/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    try { await boApi.post("/auth/invite/accept", { token, password: pw }); nav("/admin"); } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <Shell title="Accept admin invite" sub={info ? `${info.email} · ${info.role.replace("_", " ")}` : "Greenwick Back-office"}>
      {info && (
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="invite-password" type="password" placeholder="Choose a password (min 10 characters)" value={pw} onChange={(e) => setPw(e.target.value)} minLength={10} required />
          <Button data-testid="invite-submit" className="w-full">Set password</Button>
          <p className="text-xs text-muted-foreground">You will set up two-factor authentication at first sign-in.</p>
        </form>
      )}
      {err && <p className="mt-3 text-xs text-destructive">{err}</p>}
    </Shell>
  );
}
